import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../Firebaseconfig';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import AdminBar from './AdminBar';
import CompanyBar from './CompanyBar';
import CarrierBar from './CarrierBar';
import ProductBar from './ProductBar';
import Profile from './Profile';
import StatusBar from './StatusBar';
import CompanyOrders from './CompanyOrders';
import './Dashboard.css';
import { MdSpaceDashboard } from "react-icons/md";
import { ImStatsDots } from "react-icons/im";
import { TbTruckDelivery } from "react-icons/tb";
import { IoSettingsSharp } from "react-icons/io5";
import { LuPackage2 } from "react-icons/lu";
import { FaPlus } from "react-icons/fa";
import { MdGroups } from "react-icons/md";
import { ImUsers } from "react-icons/im";
import { BiNetworkChart } from "react-icons/bi";
import { FaSearch } from "react-icons/fa";
import { FaBell } from "react-icons/fa";
import logo from "../../Assets/nshpejt.png"
import { Link } from "react-router-dom";
import { CgSpinner } from "react-icons/cg";
import { useTranslation } from "react-i18next";

function MainContent({ role, activeNav, setActiveNav, refreshKey }) {
  const { t } = useTranslation();
  if (activeNav === 'settings') return <Profile key={refreshKey} />;
  if (role === 'user') return <Profile key={refreshKey} />;
  if (activeNav === 'performance' || activeNav === 'profit') return <StatusBar key={refreshKey} role={role === 'carrier' || role === 'courier' ? 'courier' : 'company'} />;

  if (role === 'admin') return <AdminBar key={refreshKey} />;
  if (role === 'company' || role === 'merchant') {
    if (activeNav === 'postProduct') return <ProductBar key={refreshKey} onBack={() => setActiveNav('dashboard')} />;
    if (activeNav === 'orders') return <CompanyOrders key={refreshKey} />;
    return <CompanyBar key={refreshKey} />;
  }
  if (role === 'carrier' || role === 'courier') return <CarrierBar key={refreshKey} />;
  return (
    <div className="placeholder-bar" key={refreshKey}>
      <div className="ph-icon">🔒</div>
      <h3>{t('dashboard.noAccess')}</h3>
      <p>{t('dashboard.unrecognizedRole')}</p>
    </div>
  );
}

function getInitials(str = '') {
  return str.trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';
}

function getFullName(profile, t) {
  if (!profile) return '';
  const first = profile.firstName || '';
  const last = profile.lastName || '';
  if (first || last) return `${first} ${last}`.trim();
  return profile.name || profile.email?.split('@')[0] || t('dashboard.unknownUser');
}

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeNav, setActiveNav] = useState('users');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setRefreshKey(prev => prev + 1);
    setTimeout(() => setIsRefreshing(false), 600);
  };

  useEffect(() => {
    let unsubSnapshot = null;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        const docRef = doc(db, 'users', user.uid);
        unsubSnapshot = onSnapshot(docRef, (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            const role = (data.role || 'user').toLowerCase();
            const photoURL = data.photoURL || user.photoURL || data.pfp || data.avatar || data.profilePic || '';
            setProfile({ uid: user.uid, ...data, photoURL, role });
          } else {
            setProfile({
              uid: user.uid,
              name: user.displayName || user.email?.split('@')[0] || 'User',
              email: user.email,
              photoURL: user.photoURL || '',
              role: 'user'
            });
          }
          setLoading(false);
        }, (err) => {
          console.error("Error listening to user document:", err);
          setProfile({
            uid: user.uid,
            name: user.displayName || user.email?.split('@')[0] || 'User',
            email: user.email,
            photoURL: user.photoURL || '',
            role: 'user'
          });
          setLoading(false);
        });
      } else {
        if (unsubSnapshot) {
          unsubSnapshot();
          unsubSnapshot = null;
        }
        setProfile(null);
        setLoading(false);
        navigate('/login');
      }
    });

    return () => {
      unsubAuth();
      if (unsubSnapshot) {
        unsubSnapshot();
      }
    };
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNavClick = (key) => {
    setActiveNav(key);
    setIsSidebarOpen(false); // Close sidebar on mobile after clicking a link
  };

  if (loading || !profile) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-root)', color: 'var(--text-secondary)', fontFamily: 'Inter,sans-serif', gap: 10 }}>
        <span className="spinner" /> {t('dashboard.loading')}
      </div>
    );
  }

  const role = (profile?.role || 'user').toLowerCase();

  const NAV = {
    admin: [{ icon: <MdGroups />, label: t('dashboard.navOrderMgmt'), key: 'orders' }, { icon: <IoSettingsSharp />, label: t('dashboard.navSettings'), key: 'settings' }],
    company: [{ icon: <MdSpaceDashboard />, label: t('dashboard.navDashboard'), key: 'dashboard' }, { icon: <ImUsers />, label: t('dashboard.navPerformance'), key: 'performance' }, { icon: <LuPackage2 />, label: t('dashboard.navOrders'), key: 'orders' }, { icon: <IoSettingsSharp />, label: t('dashboard.navSettings'), key: 'settings' }, { icon: <FaPlus />, label: t('dashboard.navPostProduct'), key: 'postProduct' }],
    carrier: [{ icon: <MdSpaceDashboard />, label: t('dashboard.navDashboard'), key: 'dashboard' }, { icon: <ImUsers />, label: t('dashboard.navProfit'), key: 'profit' }, { icon: <IoSettingsSharp />, label: t('dashboard.navSettings'), key: 'settings' }],
    courier: [{ icon: <MdSpaceDashboard />, label: t('dashboard.navDashboard'), key: 'dashboard' }, { icon: <ImUsers />, label: t('dashboard.navProfit'), key: 'profit' }, { icon: <IoSettingsSharp />, label: t('dashboard.navSettings'), key: 'settings' }],
    user: [{ icon: <IoSettingsSharp />, label: t('dashboard.navSettings'), key: 'settings' }],
  };

  const navItems = NAV[role] || NAV.admin;
  const fullName = getFullName(profile, t);
  const initials = getInitials(fullName);

  return (
    <div className="dashboard">
      {/* ── Topbar ── */}
      <div className="topbar">
        <button type="button" className="mobile-menu-btn" onClick={() => setIsSidebarOpen(!isSidebarOpen)} aria-label="Toggle navigation menu">
          ☰
        </button>

        {/* Brand (same width as sidebar) */}
        <div className="topbar-brand">
          <div className="topbar-brand-icon"><Link to={'/'}><img src={logo} alt="logo" style={{ width: 30, borderRadius: '10px' }} /></Link></div>
          <div className="topbar-brand-text">
            <strong>{t('dashboard.adminConsole')}</strong>
            <span>{t('dashboard.mgmtSuite')}</span>
          </div>
        </div>

        {/* Search */}
        <div className="topbar-search">
          <span className="topbar-search-icon"><FaSearch /></span>
          <input type="text" placeholder={t('dashboard.searchOps')} aria-label="Search operations" />
        </div>

        <div className="topbar-spacer" />

        {/* Right side */}
        <div className="topbar-right">
          <button type="button" className="topbar-icon-btn" title="Notifications" aria-label="Notifications" onClick={() => navigate('/notifications')}><FaBell /></button>
          <button type="button" className="topbar-icon-btn" title="Help" aria-label="Help">?</button>

          <div className="topbar-user-pill">
            <div className="topbar-avatar">
              {profile?.photoURL ? (
                <img src={profile.photoURL} alt="Profile" className="topbar-user-pfp" />
              ) : (
                initials
              )}
            </div>
            <div>
              <div className="topbar-user-pill-name">{fullName}</div>
              <div className="topbar-user-pill-role">{profile?.role || 'Admin'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="toolbar">
        {isSidebarOpen && (
          <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
        )}

        {/* Sidebar */}
        <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
          {/* User card */}
          <div className="sidebar-user-card">
            <div className="sidebar-user-avatar">
              {profile?.photoURL ? (
                <img src={profile.photoURL} alt="Profile" className="user-avatar" />
              ) : (
                initials
              )}
            </div>
            <div className="sidebar-user-name">{fullName}</div>
            <div className="sidebar-user-email">{profile?.email || '—'}</div>
            <div className="sidebar-user-role">● {profile?.role || 'Admin'}</div>
          </div>

          {/* Nav */}
          <div className="sidebar-nav-label">{t('dashboard.menu')}</div>
          <nav className="sidebar-nav">
            {navItems.map(item => (
              <div
                key={item.key}
                className={`sidebar-nav-item ${activeNav === item.key ? 'active' : ''}`}
                onClick={() => handleNavClick(item.key)}
              >
                <span className="nav-icon">{item.icon}</span>
                {item.label}
              </div>
            ))}
          </nav>

          {/* Bottom */}
          <div className="sidebar-bottom">
            <button
              type="button"
              className="btn-refresh-dashboard"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <span style={{
                display: 'inline-block',
                animation: isRefreshing ? 'spin 0.6s linear infinite' : 'none'
              }}>
                <CgSpinner />
              </span>
              {isRefreshing ? t('dashboard.refreshing') : t('dashboard.refreshData')}
            </button>
            <button type="button" className="btn-logout" onClick={handleLogout}>
              <span>⎋</span> {t('dashboard.logout')}
            </button>
          </div>
        </div>

        {/* Main */}
        <div className="mainbar">
          <MainContent role={role} activeNav={activeNav} setActiveNav={setActiveNav} refreshKey={refreshKey} />
        </div>
      </div>
    </div>
  );
}
